import { createContext, useContext, useEffect, useState } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion, AnimatePresence } from "framer-motion";
import { useFinePointer, EASE } from "@/lib/motion";

const CursorCtx = createContext({ setLabel: () => {} });
export const useCursor = () => useContext(CursorCtx);

export const CursorProvider = ({ children }) => {
  const [label, setLabel] = useState(null);
  const fine = useFinePointer();
  const reduce = useReducedMotion();
  const x = useMotionValue(-200);
  const y = useMotionValue(-200);
  const sx = useSpring(x, { stiffness: 500, damping: 45, mass: 0.35 });
  const sy = useSpring(y, { stiffness: 500, damping: 45, mass: 0.35 });
  const active = fine && !reduce;

  useEffect(() => {
    if (!active) return undefined;
    const mv = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
    };
    window.addEventListener("mousemove", mv, { passive: true });
    return () => window.removeEventListener("mousemove", mv);
  }, [active, x, y]);

  return (
    <CursorCtx.Provider value={{ setLabel }}>
      {children}
      {active && (
        <motion.div aria-hidden="true" className="pointer-events-none fixed left-0 top-0 z-[85]" style={{ x: sx, y: sy }}>
          <div className="-translate-x-1/2 -translate-y-1/2">
            <AnimatePresence mode="wait">
              {label ? (
                <motion.div
                  key="label"
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.4, opacity: 0 }}
                  transition={{ duration: 0.3, ease: EASE }}
                  className="flex h-[84px] w-[84px] items-center justify-center rounded-full bg-ink text-paper mono-label"
                >
                  {label}
                </motion.div>
              ) : (
                <motion.div
                  key="dot"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="h-[7px] w-[7px] rounded-full bg-white mix-blend-difference"
                />
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </CursorCtx.Provider>
  );
};
